// Drift CSV import (WI-P4-120). Everything here runs in the admin's browser:
// the two files Drift exports (Settings → Manage teammates → Export as CSV;
// Contacts → Download CSV) are parsed, their columns recognised, and the rows
// shown back before anything is sent. The server only ever receives the
// small, named fields below, never the raw file.
//
// Pre-implementation research:
// - No CSV parser exists in site/, portal/ or widget/ (this is the first).
// - Column names are not published by Drift and vary by era, so matching is
//   by normalised header with aliases, and the admin sees the mapping.

export const TEAMMATE_FIELDS = {
  email: ["email", "emailaddress", "workemail", "teammateemail", "useremail"],
  name: ["name", "fullname", "teammate", "teammatename", "displayname", "username"],
  role: ["role", "roles", "permission", "permissions", "accesslevel", "userrole", "seat", "seattype"],
};

export const CONTACT_FIELDS = {
  external_id: ["id", "contactid", "driftid", "driftcontactid", "enduserid", "userid", "contact"],
  email: ["email", "emailaddress", "workemail", "contactemail", "primaryemail"],
  name: ["name", "fullname", "contactname", "displayname"],
  organization: ["company", "companyname", "organization", "organisation", "account", "accountname", "employer"],
};

const FIRST_NAME = ["firstname", "first", "givenname"];
const LAST_NAME = ["lastname", "last", "surname", "familyname"];

/** Parse CSV text (RFC 4180 quoting, CRLF or LF, optional BOM) into rows of strings. */
export function parseCSV(text) {
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  let i = 0;
  const src = String(text || "").replace(/^﻿/, "");
  while (i < src.length) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        quoted = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      quoted = true;
      i += 1;
      continue;
    }
    if (ch === ",") {
      row.push(field);
      field = "";
      i += 1;
      continue;
    }
    if (ch === "\r") {
      i += 1;
      continue;
    }
    if (ch === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      i += 1;
      continue;
    }
    field += ch;
    i += 1;
  }
  if (field !== "" || row.length) {
    row.push(field);
    rows.push(row);
  }
  // Drop rows that are entirely blank (trailing newline, spacer lines).
  return rows.filter((r) => r.some((cell) => String(cell).trim() !== ""));
}

/** "Email Address" → "emailaddress": the key both aliases and headers are compared on. */
export function normalizeHeader(header) {
  return String(header || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

/**
 * Match CSV headers to the fields we need. Returns {mapping: {field: columnIndex},
 * nameParts: {first, last} | null, unmatched: [header...]}. The first header that
 * matches an alias wins; a field without a match is simply absent.
 */
export function detectColumns(headers, fields) {
  const normalised = headers.map(normalizeHeader);
  const mapping = {};
  const used = new Set();
  for (const [field, aliases] of Object.entries(fields)) {
    for (const alias of aliases) {
      const idx = normalised.findIndex((h, j) => h === alias && !used.has(j));
      if (idx !== -1) {
        mapping[field] = idx;
        used.add(idx);
        break;
      }
    }
  }
  let nameParts = null;
  if (mapping.name === undefined) {
    const first = normalised.findIndex((h) => FIRST_NAME.includes(h));
    const last = normalised.findIndex((h) => LAST_NAME.includes(h));
    if (first !== -1 || last !== -1) {
      nameParts = { first, last };
      if (first !== -1) used.add(first);
      if (last !== -1) used.add(last);
    }
  }
  const unmatched = headers.filter((_, j) => !used.has(j));
  return { mapping, nameParts, unmatched };
}

function cell(row, idx) {
  if (idx === undefined || idx === -1) return "";
  return String(row[idx] ?? "").trim();
}

function nameFrom(row, mapping, nameParts) {
  if (mapping.name !== undefined) return cell(row, mapping.name);
  if (!nameParts) return "";
  return [cell(row, nameParts.first), cell(row, nameParts.last)].filter(Boolean).join(" ");
}

/**
 * Turn Drift's teammates CSV into import rows. Result: {rows, columns, skipped,
 * headers, error}. `error` is set when no e-mail column could be found.
 */
export function parseTeammatesCSV(text) {
  const parsed = parseCSV(text);
  if (!parsed.length) return { rows: [], columns: {}, skipped: 0, headers: [], error: "The file is empty." };
  const [headers, ...body] = parsed;
  const { mapping, nameParts } = detectColumns(headers, TEAMMATE_FIELDS);
  if (mapping.email === undefined) {
    return { rows: [], columns: {}, skipped: 0, headers, error: "No e-mail column found. The teammates export needs an Email column." };
  }
  const rows = [];
  let skipped = 0;
  for (const row of body) {
    const email = cell(row, mapping.email);
    const name = nameFrom(row, mapping, nameParts);
    if (!email && !name) {
      skipped += 1;
      continue;
    }
    rows.push({ email, name, role: cell(row, mapping.role) });
  }
  return { rows, columns: describeColumns(headers, mapping, nameParts), skipped, headers, error: "" };
}

/**
 * Turn Drift's contacts CSV into import rows. Needs an id or an e-mail column.
 */
export function parseContactsCSV(text) {
  const parsed = parseCSV(text);
  if (!parsed.length) return { rows: [], columns: {}, skipped: 0, headers: [], error: "The file is empty." };
  const [headers, ...body] = parsed;
  const { mapping, nameParts } = detectColumns(headers, CONTACT_FIELDS);
  if (mapping.external_id === undefined && mapping.email === undefined) {
    return { rows: [], columns: {}, skipped: 0, headers, error: "No ID or e-mail column found. The contacts export needs one of them." };
  }
  const rows = [];
  let skipped = 0;
  for (const row of body) {
    const external_id = cell(row, mapping.external_id);
    const email = cell(row, mapping.email);
    if (!external_id && !email) {
      skipped += 1;
      continue;
    }
    rows.push({ external_id, email, name: nameFrom(row, mapping, nameParts), organization: cell(row, mapping.organization) });
  }
  return { rows, columns: describeColumns(headers, mapping, nameParts), skipped, headers, error: "" };
}

function describeColumns(headers, mapping, nameParts) {
  const out = {};
  for (const [field, idx] of Object.entries(mapping)) out[field] = headers[idx];
  if (nameParts) {
    out.name = [headers[nameParts.first], headers[nameParts.last]].filter(Boolean).join(" + ");
  }
  return out;
}

/** Split rows into request-sized chunks; the server caps each request. */
export function chunk(rows, size) {
  const out = [];
  for (let i = 0; i < rows.length; i += size) out.push(rows.slice(i, i + size));
  return out;
}

/** Plain words for an outcome, in the past or conditional tense. */
export function outcomeLabel(outcome, dryRun) {
  switch (outcome) {
    case "invite":
      return dryRun ? "Will be invited" : "Invited";
    case "create":
      return dryRun ? "Will be added" : "Added";
    case "already_member":
      return "Already a member";
    case "already_invited":
      return "Invite already pending";
    case "exists":
      return "Already imported";
    case "duplicate":
      return "Repeated in file";
    case "invalid":
      return "Skipped";
    case "failed":
      return "Failed";
    default:
      return outcome || "";
  }
}

/** Pill tone per outcome: ok (a change), dim (nothing to do), warn (needs a look). */
export function outcomeTone(outcome) {
  if (outcome === "invite" || outcome === "create") return "ok";
  if (outcome === "invalid" || outcome === "failed") return "warn";
  return "dim";
}
